import styles from 'css/src/components/common/video-preview/VideoWithScrubber.module.scss';

import React from 'react';
import type Immutable from 'immutable';
import debounce from 'lodash/debounce';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { SeekMode } from '@lumen5/luminary/enums';

import Tooltip from 'js/src/components/common/ui/Tooltip';
import { nextPlaybackStateOnSeek } from 'js/src/components/common/video-preview/playbackStateOnSeek';
import SlideMarkerScrubber from 'js/src/components/common/video-preview/SlideMarkerScrubber';
import { PLAYBACK_STATE } from 'js/src/components/common/video-preview/VideoPreview';
import { enqueueTask, TASK_NAMES } from 'js/src/libs/luminary-task-queue';
import { sentryCanvasSnapshot } from 'js/src/libs/sentry';
import Timer from 'js/src/libs/timer';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import { slideEndTimes, slideIdsByStartTime, slideStartTimes } from 'js/src/luminary';
import {
    createLuminaryOperation,
    useLuminaryOperations,
    useLuminarySelector,
} from 'js/src/luminary/luminary-hooks';

const PAUSE_BEFORE_END_MS = 150;

const LUMINARY_EVENTS = ['stateChanged', 'onCanPlay'];

// hacked-in event handlers - not ideal, but workable for now
const luminaryOnOperation = createLuminaryOperation(
    ({ luminary }, event: string, callback: () => void) => {
        luminary.on(event, callback);
    },
);
const luminaryOffOperation = createLuminaryOperation(
    ({ luminary }, event: string, callback: () => void) => {
        luminary.off(event, callback);
    },
);
const luminaryOnceOperation = createLuminaryOperation(
    ({ luminary }, event: string, callback: () => void) => {
        luminary.once(event, callback);
    },
);
const luminaryOnCanPlayOperation = createLuminaryOperation(({ luminary }) => luminary.onCanPlay());
const getLuminaryCurrentTimeOperation = createLuminaryOperation(
    ({ luminary }) => luminary.currentTime,
);
const luminarySeekOperation = createLuminaryOperation(
    async ({ luminary }, seekTime: number, seekMode?: SeekMode) => {
        await luminary.seek(seekTime, seekMode);
        if (!luminary.isPlaying()) {
            luminary.render();
        }
        if (!luminary.canvas) {
            return;
        }
        await sentryCanvasSnapshot(luminary.canvas);
    },
);
const luminarySetCanvasParentOperation = createLuminaryOperation(
    ({ luminary }, canvasParent: HTMLDivElement | null) => {
        luminary.canvasParent = canvasParent;
    },
);
const luminaryPlayOperation = createLuminaryOperation(
    ({ luminary }, range?: Parameters<Luminary['play']>[0]) => luminary.play(range),
);
const luminaryPauseOperation = createLuminaryOperation(({ luminary }) => {
    luminary.pause();
});
const luminaryStopOperation = createLuminaryOperation(({ luminary }) => {
    luminary.stop();
});
const luminarySetMutedOperation = createLuminaryOperation(({ luminary }, muted: boolean) => {
    luminary.muted = muted;
});
const sendCanvasSnapshotOperation = createLuminaryOperation(async ({ luminary }) => {
    if (luminary.isPlaying() && luminary.canvas) {
        await sentryCanvasSnapshot(luminary.canvas);
    }
});

// Props are accessed via propsRef.current to avoid stale closures in timer callbacks.
// ESLint can't trace usage through this pattern, so we disable the rule for this type.
/* eslint-disable react/no-unused-prop-types */
type LuminaryVideoWithScrubberOwnProps = {
    autoplay?: boolean;
    landscapeContainer?: boolean;
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: Immutable.Map<string, number>;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    playbackState: PLAYBACK_STATE;
    setPlaybackState: (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => void;
    muted: boolean;
    onCurrentTimeUpdate?: (currentTime: number) => void;
    scrubberTooltipTitle?: React.ReactNode;
    /**
     * Time (ms) to seek to on mount instead of the default "middle of first slide" poster frame.
     * Sentinel semantics: `undefined` or `0` means "no preference, use the default poster" — a
     * legitimate desire to seek to exactly 0 is indistinguishable from "no preference".
     * Values are clamped to `[0, duration]` to tolerate stale times from a regenerated video.
     */
    initialSeekTime?: number;
};
/* eslint-enable react/no-unused-prop-types */

export type LuminaryVideoWithScrubberHandle = {
    play: (opts?: { firstPlay: boolean }) => Promise<void>;
    pause: () => Promise<void>;
    stop: () => Promise<void>;
    getCurrentTime: () => number;
    getDuration: () => number;
    seek: (time: number) => void;
};

type CancellablePromise = {
    promise: JQuery.Deferred<unknown>;
    cancel: () => void;
};

const LuminaryVideoWithScrubber = React.memo(function LuminaryVideoWithScrubber(
    props: LuminaryVideoWithScrubberOwnProps & {
        ref?: React.Ref<LuminaryVideoWithScrubberHandle>;
    },
) {
    const { ref } = props;
    // Destructure only props used directly in render
    // Other props accessed via propsRef.current to avoid stale closures in timers/callbacks
    const {
        numOpenCommentsPerScene,
        commentMarkerCallback,
        playbackState,
        muted,
        landscapeContainer,
        scrubberTooltipTitle,
    } = props;

    const {
        luminaryCanPlay,
        duration,
        aspectRatio,
        firstSceneDuration,
        slideStartTimes: slideStartTimesProp,
        slideEndTimes: slideEndTimesProp,
        slideIdsByStartTime: slideIdsByStartTimeProp,
    } = useLuminarySelector(
        (luminary) => ({
            luminaryCanPlay: luminary.canPlay,
            duration: luminary.duration,
            aspectRatio: luminary.aspectRatio,
            firstSceneDuration: luminary.scenes[0]?.duration ?? 0,
            slideStartTimes: slideStartTimes(luminary),
            slideEndTimes: slideEndTimes(luminary),
            slideIdsByStartTime: slideIdsByStartTime(luminary),
        }),
        [],
        { events: LUMINARY_EVENTS },
    );

    const {
        luminaryOn,
        luminaryOff,
        luminaryOnce,
        luminaryOnCanPlay,
        getLuminaryCurrentTime,
        luminarySeek,
        luminarySetCanvasParent,
        luminaryPlay,
        luminaryPause,
        luminaryStop,
        luminarySetMuted,
        sendCanvasSnapshot,
    } = useLuminaryOperations({
        luminaryOnOperation,
        luminaryOffOperation,
        luminaryOnceOperation,
        luminaryOnCanPlayOperation,
        getLuminaryCurrentTimeOperation,
        luminarySeekOperation,
        luminarySetCanvasParentOperation,
        luminaryPlayOperation,
        luminaryPauseOperation,
        luminaryStopOperation,
        luminarySetMutedOperation,
        sendCanvasSnapshotOperation,
    });

    const landscapeContainerClass = landscapeContainer ? ' landscape-container' : '';

    const [currentTimeState, setCurrentTimeState] = React.useState(0);
    const [readyToPlay, setReadyToPlay] = React.useState(false);

    const luminaryPreviewRef = React.useRef<HTMLDivElement>(null);
    const progressBarRef = React.useRef<HTMLSpanElement>(null);
    const clockTimeoutRef = React.useRef<Timer | null>(null);
    const promisesRef = React.useRef<CancellablePromise[]>([]);

    // Refs to always access current values (avoid stale closures in callbacks/timers)
    const propsRef = React.useRef(props);
    propsRef.current = props;

    const luminaryStateRef = React.useRef({
        luminaryCanPlay,
        duration,
        firstSceneDuration,
        slideStartTimes: slideStartTimesProp,
        slideIdsByStartTime: slideIdsByStartTimeProp,
    });
    luminaryStateRef.current = {
        luminaryCanPlay,
        duration,
        firstSceneDuration,
        slideStartTimes: slideStartTimesProp,
        slideIdsByStartTime: slideIdsByStartTimeProp,
    };

    const stateRef = React.useRef({ currentTimeState, readyToPlay });
    stateRef.current = { currentTimeState, readyToPlay };

    const pushPromise = React.useCallback(<T,>(promise: Promise<T>): JQuery.Deferred<T> => {
        let hasCanceled = false;
        const wrappedPromise = $.Deferred<T>();

        promise.then(
            (val) => {
                // Remove from list
                promisesRef.current = promisesRef.current.filter(
                    (p) => p.promise !== wrappedPromise,
                );
                if (hasCanceled) {
                    wrappedPromise.reject(new Error('_cancelled_'));
                } else {
                    wrappedPromise.resolve(val);
                }
            },
            (error) => {
                promisesRef.current = promisesRef.current.filter(
                    (p) => p.promise !== wrappedPromise,
                );
                if (hasCanceled) {
                    wrappedPromise.reject(new Error('_cancelled_'));
                } else {
                    wrappedPromise.reject(error);
                }
            },
        );

        const cancelable: CancellablePromise = {
            promise: wrappedPromise,
            cancel: () => {
                hasCanceled = true;
            },
        };
        promisesRef.current.push(cancelable);
        return wrappedPromise;
    }, []);

    const setCurrentTimeWithCallback = React.useCallback((newTime: number): Promise<void> => {
        return new Promise<void>((resolve) => {
            setCurrentTimeState(newTime);
            // Use requestAnimationFrame to ensure state has updated before callback
            requestAnimationFrame(() => {
                const { onCurrentTimeUpdate: onUpdate } = propsRef.current;
                if (typeof onUpdate === 'function') {
                    onUpdate(newTime);
                }
                resolve();
            });
        });
    }, []);

    const pauseClock = React.useCallback(() => {
        if (clockTimeoutRef.current !== null) {
            clockTimeoutRef.current.pause();
        }
    }, []);

    const stopClock = React.useCallback(() => {
        if (clockTimeoutRef.current !== null) {
            clockTimeoutRef.current.cancel();
            clockTimeoutRef.current = null;
        }
        setCurrentTimeState(0);
    }, []);

    const setTimer = React.useCallback(() => {
        const UPDATE_INTERVAL = 100;
        clockTimeoutRef.current = new Timer(() => {
            // Read current values from refs to avoid stale closures
            const { playbackState: state } = propsRef.current;
            const { duration: dur } = luminaryStateRef.current;
            const time = getLuminaryCurrentTime();
            setCurrentTimeState(time);

            const { onCurrentTimeUpdate: onUpdate } = propsRef.current;
            if (typeof onUpdate === 'function') {
                onUpdate(time);
            }

            // To achieve the "replay" state we want to check if we're very close to the end
            // If we are, pause early to preserve last visible frame!
            const timeUntilEnd = dur - time;
            if (timeUntilEnd <= PAUSE_BEFORE_END_MS && timeUntilEnd > 0) {
                luminaryPause();
                pauseClock();
                setCurrentTimeState(dur);
                if (typeof onUpdate === 'function') {
                    onUpdate(dur);
                }
                propsRef.current.setPlaybackState(PLAYBACK_STATE.STOPPED);
                return;
            }

            if (state === PLAYBACK_STATE.PLAYING) {
                setTimer();
            }
        }, UPDATE_INTERVAL);
    }, [pauseClock, getLuminaryCurrentTime, luminaryPause]);

    const playClock = React.useCallback(() => {
        if (clockTimeoutRef.current) {
            clockTimeoutRef.current.resume();
        } else {
            setTimer();
        }
    }, [setTimer]);

    const instantSeekProgressBar = React.useCallback(
        (newTime: number) => {
            let oldTransitionDuration = '100ms';
            if (progressBarRef.current) {
                oldTransitionDuration = window.getComputedStyle(
                    progressBarRef.current,
                ).transitionDuration;
                progressBarRef.current.style.transitionDuration = '0s';
            }
            setCurrentTimeState(newTime);

            const {
                onCurrentTimeUpdate: onUpdate,
                playbackState: currentState,
                setPlaybackState: setState,
            } = propsRef.current;
            const { duration: dur } = luminaryStateRef.current;
            if (typeof onUpdate === 'function') {
                onUpdate(newTime);
            }

            const nextState = nextPlaybackStateOnSeek(currentState, newTime, dur);
            if (nextState !== currentState) {
                setState(nextState);
            }

            requestAnimationFrame(() => {
                if (progressBarRef.current) {
                    progressBarRef.current.style.transitionDuration = oldTransitionDuration;
                }
                if (propsRef.current.playbackState === PLAYBACK_STATE.PLAYING) {
                    playClock();
                }
            });
        },
        [playClock],
    );

    // Use useMemo for the debounced seek to maintain stable identity
    const seek = React.useMemo(
        () =>
            debounce(
                (newTime: number) => {
                    instantSeekProgressBar(newTime);
                    pushPromise(luminarySeek(newTime, VideoData.SeekMode.ResolveWhenCanPlay));

                    const { onSeek: onSeekCallback } = propsRef.current;
                    if (typeof onSeekCallback === 'function') {
                        onSeekCallback(newTime);
                    }
                },
                250,
                { leading: true, trailing: true },
            ),
        [instantSeekProgressBar, pushPromise, luminarySeek],
    );

    const luminaryOnPlayComplete = React.useCallback(() => {
        // This event fires after playback completes, but we handle the transition
        // early in setTimer to preserve the last visible frame, so this is a no-op unless
        // the playback state isn't STOPPED
        const { playbackState: state, setPlaybackState: setState } = propsRef.current;
        const { duration: dur } = luminaryStateRef.current;
        if (state !== PLAYBACK_STATE.STOPPED) {
            pauseClock();
            setCurrentTimeState(dur);
            const { onCurrentTimeUpdate: onUpdate } = propsRef.current;
            if (typeof onUpdate === 'function') {
                onUpdate(dur);
            }
            setState(PLAYBACK_STATE.STOPPED);
        }
    }, [pauseClock]);

    const pause = React.useCallback(() => {
        return new Promise<void>((resolve) => {
            const { playbackState: state, setPlaybackState: setState } = propsRef.current;
            if (state === PLAYBACK_STATE.LOADING) {
                resolve();
                return;
            }
            luminaryOff('pause', pause);
            setState(PLAYBACK_STATE.PAUSED, () => {
                pauseClock();
                luminaryPause();
                resolve();
            });
        });
    }, [pauseClock, luminaryOff, luminaryPause]);

    const stop = React.useCallback(() => {
        return new Promise<void>((resolve) => {
            setCurrentTimeWithCallback(0).then(() => {
                propsRef.current.setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                    stopClock();
                    luminaryStop();
                    resolve();
                });
            });
        });
    }, [setCurrentTimeWithCallback, stopClock, luminaryStop]);

    // Returns the clamped `initialSeekTime` if it's set to a meaningful value, else null.
    // Sentinel: 0/undefined => "no preference" (caller decides the default).
    const getClampedInitialSeekTime = React.useCallback((): number | null => {
        const { initialSeekTime } = propsRef.current;
        const { duration: totalDuration } = luminaryStateRef.current;
        if (initialSeekTime === undefined || initialSeekTime <= 0) return null;
        return Math.max(0, Math.min(initialSeekTime, totalDuration));
    }, []);

    // Picks the time to seek to when entering the paused/poster state: caller-supplied
    // `initialSeekTime` wins, otherwise fall back to a frame halfway through the first slide.
    const computeInitialSeekTime = React.useCallback((): number => {
        const clamped = getClampedInitialSeekTime();
        if (clamped !== null) return clamped;

        const {
            slideStartTimes: currentStartTimes,
            firstSceneDuration: currentFirstDur,
            duration: totalDuration,
            slideIdsByStartTime,
        } = luminaryStateRef.current;
        const isSingleScene = slideIdsByStartTime.size <= 1;
        return isSingleScene ? totalDuration / 2 : currentStartTimes[0] + currentFirstDur / 2;
    }, [getClampedInitialSeekTime]);

    const init = React.useCallback(() => {
        if (!stateRef.current.readyToPlay) {
            // try again in a little bit
            window.setTimeout(() => {
                init();
            }, 100);
            return;
        }

        const { setPlaybackState: setState } = propsRef.current;
        const { luminaryCanPlay: canPlay } = luminaryStateRef.current;

        // Check first if video resources have been loaded first
        if (!canPlay) {
            setState(PLAYBACK_STATE.LOADING);
            pushPromise(luminaryOnCanPlay()).then(() => {
                init();
            });
            return;
        }

        setState(PLAYBACK_STATE.PAUSED, () => {
            luminarySetCanvasParent(luminaryPreviewRef.current);
            const target = computeInitialSeekTime();
            // Only sync the scrubber when an explicit initialSeekTime was supplied; the
            // default poster-frame leaves the scrubber at 0 (preserving prior behaviour).
            if (getClampedInitialSeekTime() !== null) {
                setCurrentTimeState(target);
            }
            luminarySeek(target);
        });
    }, [
        pushPromise,
        computeInitialSeekTime,
        getClampedInitialSeekTime,
        luminaryOnCanPlay,
        luminarySetCanvasParent,
        luminarySeek,
    ]);

    const play = React.useCallback(
        (opts = { firstPlay: true }) => {
            return new Promise<void>((resolve) => {
                if (!stateRef.current.readyToPlay) {
                    // try again in a little bit
                    window.setTimeout(() => {
                        play(opts);
                    }, 100);
                    return;
                }

                const {
                    setPlaybackState: setState,
                    autoplay: auto,
                    playbackState: state,
                } = propsRef.current;
                const { luminaryCanPlay: canPlay } = luminaryStateRef.current;

                // Check first if video resources have been loaded first
                if (!canPlay) {
                    setState(PLAYBACK_STATE.LOADING);
                    pushPromise(luminaryOnCanPlay()).then(() => {
                        play({ firstPlay: true });
                    });
                    return;
                }

                const playFromStart =
                    opts.firstPlay ||
                    state === PLAYBACK_STATE.STOPPED ||
                    (!auto && stateRef.current.currentTimeState === 0); // edge case to avoid video starting at outro slide

                setState(PLAYBACK_STATE.PLAYING, () => {
                    luminarySetCanvasParent(luminaryPreviewRef.current);
                    const startTime = playFromStart ? 0 : undefined;
                    luminaryPlay({ startTime });
                    luminaryOnce('playStart', () => {
                        playClock();
                        resolve();
                    });
                    luminaryOff('pause', pause);
                    luminaryOnce('pause', pause);
                });
            });
        },
        [
            pause,
            playClock,
            pushPromise,
            luminaryOnCanPlay,
            luminarySetCanvasParent,
            luminaryPlay,
            luminaryOnce,
            luminaryOff,
        ],
    );

    React.useImperativeHandle(
        ref,
        () => ({
            play,
            pause,
            stop,
            getCurrentTime: () => stateRef.current.currentTimeState,
            getDuration: () => luminaryStateRef.current.duration,
            seek,
        }),
        [play, pause, stop, seek],
    );

    // Handle muted prop changes
    const prevMutedRef = React.useRef(muted);
    React.useEffect(() => {
        if (muted !== prevMutedRef.current) {
            luminarySetMuted(muted);
            prevMutedRef.current = muted;
        }
    }, [muted, luminarySetMuted]);

    // Mount effect
    React.useEffect(() => {
        luminaryOn('playComplete', luminaryOnPlayComplete);
        luminaryOn('onRenderComplete', sendCanvasSnapshot);

        const seekToStartTask = () => {
            return new Promise<void>((resolve) => {
                const { autoplay: auto } = propsRef.current;
                const { luminaryCanPlay: canPlay } = luminaryStateRef.current;
                luminaryStop();
                if (canPlay) {
                    // Honour initialSeekTime so autoplay-from-handoff resumes at the
                    // requested frame; otherwise seek to 0 to wait for play() (which
                    // handles its own start frame). The poster-frame seek for the
                    // non-autoplay path happens later in init().
                    const clamped = getClampedInitialSeekTime();
                    const startTime = clamped ?? 0;
                    luminarySeek(startTime);
                    if (startTime > 0) {
                        setCurrentTimeState(startTime);
                    }
                }
                setReadyToPlay(true);
                if (!auto) {
                    init();
                }
                resolve();
            });
        };

        enqueueTask({
            callback: seekToStartTask,
            taskName: TASK_NAMES.VIDEO_PREVIEW_SEEK_TO_START,
        });

        luminarySetMuted(muted);

        return () => {
            // interrupt & cancel any animations, clocks, etc
            if (clockTimeoutRef.current !== null) {
                clockTimeoutRef.current.cancel();
                clockTimeoutRef.current = null;
            }
            luminaryOff('pause', pause);
            luminaryOff('playComplete', luminaryOnPlayComplete);
            luminaryOff('onRenderComplete', sendCanvasSnapshot);
            luminaryStop();
            // Cancel all pending promises
            promisesRef.current.forEach((p) => p.cancel());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const progressBarPercentage = (currentTimeState * 100.0) / duration;

    const progressScrubber = (
        <div className={`d-flex align-items-center ${styles['slide-meta']}`}>
            <div className="progress slide-meta-overlay">
                <span
                    ref={progressBarRef}
                    className="progress-bar progress-bar-grey progress-bar-second-transitions w-100"
                    style={{
                        transform: `scaleX(${progressBarPercentage / 100})`,
                    }}
                />
            </div>
            {(playbackState === PLAYBACK_STATE.PLAYING ||
                playbackState === PLAYBACK_STATE.PAUSED ||
                playbackState === PLAYBACK_STATE.STOPPED) && (
                <SlideMarkerScrubber
                    slideStartTimes={slideStartTimesProp}
                    slideEndTimes={slideEndTimesProp}
                    slideIdsByStartTime={slideIdsByStartTimeProp}
                    numOpenCommentsPerScene={numOpenCommentsPerScene}
                    duration={duration}
                    seek={seek}
                    commentMarkerCallback={commentMarkerCallback}
                />
            )}
            <div className={`position-absolute start-0 ms-l5-8 pe-none ${styles['blur-text-bkg']}`}>
                {getTimeStringFromSeconds(currentTimeState / 1000)}
            </div>
            <div className={`position-absolute end-0 me-l5-8 pe-none ${styles['blur-text-bkg']}`}>
                {getTimeStringFromSeconds(duration / 1000)}
            </div>
        </div>
    );

    return (
        <div className="col-md-12 luminary-video-with-scrubber">
            <div className="slide-corner-radius slide-shadow slide-wrapper">
                {playbackState === PLAYBACK_STATE.LOADING ? (
                    <div className="pe-none">
                        <div
                            className={`luminary-canvas top-corner-radius text-center ${aspectRatio}${landscapeContainerClass}`}
                        >
                            <div className="d-flex position-absolute w-100 h-100">
                                <span className="m-l5-auto">Loading...</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`luminary-canvas top-corner-radius pe-none ${aspectRatio}${landscapeContainerClass}`}
                    >
                        <div
                            className="luminary-preview"
                            ref={luminaryPreviewRef}
                            data-testid="luminary-preview"
                        />
                    </div>
                )}
                {scrubberTooltipTitle ? (
                    <Tooltip title={scrubberTooltipTitle}>{progressScrubber}</Tooltip>
                ) : (
                    progressScrubber
                )}
            </div>
        </div>
    );
});

export default LuminaryVideoWithScrubber;
